import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { IdGeneratorGateway } from '@core/services/id-generator.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import type { MessageTone } from '@core/feedback/feedback.config'

// Same four words as feedback, for the same reason — declared once in
// feedback.config.ts so the two cannot drift into lookalike unions.
export type NotificationType = MessageTone

export type Notification = {
  id: string
  type: NotificationType
  message: string
}

const DEFAULT_DURATION_MS = 4000

/**
 * The toast stack. State lives here, not on the Presenter, because callers
 * across the app raise notifications — and a caller wanting `notify()` is
 * asking for behaviour, not for a ViewModel. With the list on the Presenter,
 * every one of those callers had to inject a Presenter into a Presenter,
 * which made a plain service call look like §9.3 page coordination.
 *
 * Deliberately NOT a `UserScopedState` participant. This is the channel chosen
 * for messages that must outlive a session transition — `AppPresenter` raises
 * "your session expired" here, after `logOut()`, precisely because the feedback
 * list would be wiped by the redirect that follows.
 */
@injectable()
export class NotificationRepository {
  notificationListPm: readonly Notification[] = []

  @inject(Types.IIdGeneratorGateway)
  private idGenerator!: IdGeneratorGateway

  // Auto-dismiss goes through the clock gateway rather than setTimeout: a timer
  // is environment-coupled and nondeterministic, so it belongs behind a Gateway
  // like every other browser API (§C).
  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  constructor() {
    makeObservable(this, {
      notificationListPm: observable.ref,
      notify: action,
      dismiss: action,
    })
  }

  // duration: ms before auto-dismiss, or null to require an explicit dismiss()
  // (e.g. for something the user needs to actually read/act on).
  notify(type: NotificationType, message: string, duration: number | null = DEFAULT_DURATION_MS): string {
    const id = this.idGenerator.generate()
    this.notificationListPm = [...this.notificationListPm, { id, type, message }]

    if (duration !== null) {
      this.clockGateway.after(duration, () => this.dismiss(id))
    }

    return id
  }

  dismiss(id: string) {
    this.notificationListPm = this.notificationListPm.filter((notification) => notification.id !== id)
  }
}
